<script lang="ts">
    import {_} from 'svelte-i18n'
    import Dialog, {Actions, Content, Title} from '@smui/dialog';
    import Button, {Label} from '@smui/button';
    import FormField from '@smui/form-field';
    import Checkbox from '@smui/checkbox';
    import type {DialogCancel, UserSaveRequest} from './types';
    import Textfield from '@smui/textfield';
    import HelperText from '@smui/textfield/helper-text';

    const CONFIRM_ACTION_NAME = 'dialog-confirm';
    const PASSWORD_REGEX = /^(?=.*[a-záčďéěíňóřšťúůýžA-ZÁČĎÉĚÍŇÓŘŠŤÚŮÝŽ])(?=.*[0-9]).{6,}$/;
    const PASSWORD_VALIDATION_MESSAGE = 'Heslo musí obsahovat minimálně 6 znaků, z toho alespoň 1 číslici.';

    export let usernameEdit: boolean;
    export let currentPasswordRequired: boolean;
    export let passwordRequired: boolean;
    export let passwordEdit: boolean;
    export let operatorRoleEdit: boolean;
    export let intervisorRoleEdit: boolean;

    let userSaveRequest: UserSaveRequest;
    let dialogOpened;
    let passwordConfirmation: string = '';
    let formValid: boolean = false;

    $: {
        formValid = true;

        if (!userSaveRequest) {
            formValid = false;
        }

        if (formValid && usernameEdit) {
            if (userSaveRequest.username.length === 0) {
                formValid = false;
            }
        }

        if (formValid && passwordEdit) {
            if (userSaveRequest.password !== passwordConfirmation) {
                formValid = false;
            }
            if (passwordRequired) {
                if (userSaveRequest.password.length === 0) {
                    formValid = false;
                }
            }
            if (userSaveRequest.password.length > 0) {
                if (!userSaveRequest.password.match(PASSWORD_REGEX)) {
                    formValid = false;
                }
            }
            if (currentPasswordRequired) {
                if (userSaveRequest.currentPassword.length === 0) {
                    formValid = false;
                }
            }
        }
    }

    let dialogResolve;
    let dialogReject;

    function submitResult() {
        if (!formValid) {
            return;
        }
        dialogResolve(userSaveRequest);
    }

    function cancelResult() {
        const dialogCancel: DialogCancel = {
            byUser: true
        };
        dialogReject(dialogCancel);
    }

    function closeHandler(e: CustomEvent) {
        if (e.detail.action === CONFIRM_ACTION_NAME) {
            return submitResult();
        }
        return cancelResult();
    }

    function onFormSubmit(e: CustomEvent<void>) {
        dialogOpened = false;
    }

    export async function open(dialogUserSaveRequest: UserSaveRequest) {
        userSaveRequest = dialogUserSaveRequest;
        passwordConfirmation = '';
        dialogOpened = true;
        return await new Promise<UserSaveRequest>((resolve, reject) => {
            dialogResolve = resolve;
            dialogReject = reject;
        });
    }
</script>

<Dialog bind:open={dialogOpened} aria-labelledby="dialog-title" aria-describedby="dialog-content" on:MDCDialog:closed={closeHandler}>
    {#if userSaveRequest}
        <Title id="dialog-title-user-edit">{$_('user.User')} {userSaveRequest.username}</Title>
        <Content id="dialog-content">
            <div style="margin: 20px 0 5px 0;">

                <form on:submit|preventDefault={onFormSubmit}>
                    {#if usernameEdit}
                        <div>
                            <Textfield fullwidth bind:value={userSaveRequest.username} label={$_('user.auth.Username')} input$aria-describedby="operator-username-input-helper-text" />
                            <HelperText id="operator-username-input-helper-text"> </HelperText>
                        </div>
                    {/if}
                    {#if passwordEdit}
                        {#if currentPasswordRequired}
                            <div>
                                <Textfield type="password" fullwidth bind:value={userSaveRequest.currentPassword} label={$_('user.auth.CurrentPassword')} input$aria-describedby="operator-current-password-input-helper-text" />
                                <HelperText id="operator-current-password-input-helper-text"> </HelperText>
                            </div>
                        {/if}
                        <div>
                            <Textfield type="password" fullwidth bind:value={userSaveRequest.password} label={$_('user.auth.NewPassword')} input$aria-describedby="operator-password-input-helper-text" />
                            <HelperText id="operator-password-input-helper-text">{PASSWORD_VALIDATION_MESSAGE}</HelperText>
                        </div>
                        <div>
                            <Textfield type="password" fullwidth bind:value={passwordConfirmation} label={$_('user.auth.RepeatPassword')} input$aria-describedby="operator-password-confirmation-input-helper-text" />
                            <HelperText id="operator-password-confirmation-input-helper-text"> </HelperText>
                        </div>
                    {/if}
                    {#if operatorRoleEdit}
                        <FormField>
                            <Checkbox bind:checked={userSaveRequest.operator} />
                            <span slot="label">{$_('user.Consultant')}</span>
                        </FormField>
                    {/if}
                    {#if intervisorRoleEdit}
                        <FormField>
                            <Checkbox bind:checked={userSaveRequest.intervisor} />
                            <span slot="label">{$_('user.Intervisor')}</span>
                        </FormField>
                    {/if}
                </form>

            </div>
        </Content>
        <Actions>
            <Button action="close">
                <Label>{$_('commons.Storno')}</Label>
            </Button>
            <Button action="dialog-confirm" disabled={!formValid}>
                <Label>{$_('commons.Save')}</Label>
            </Button>
        </Actions>
    {/if}
</Dialog>
